import React, { useState, useEffect } from 'react';
import { getFirestore, doc, getDoc, setDoc } from 'firebase/firestore';
import { useAuth } from '../../context/AuthContext';
import { useBusinessConfig } from '../../context/BusinessConfigContext';
import { MessageSquare, Save, X, Edit2, AlertCircle } from 'lucide-react';

export function WelcomeTextManager() {
  const [welcomeText, setWelcomeText] = useState('');
  const [originalText, setOriginalText] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  const { user } = useAuth();
  const { config } = useBusinessConfig();

  // Get business slug from config
  const businessSlug = config?.business_slug;

  useEffect(() => {
    loadWelcomeText();
  }, [businessSlug]);

  const loadWelcomeText = async () => {
    if (!businessSlug) return;

    try {
      setLoading(true);
      setError(null);

      const db = getFirestore();
      const docRef = doc(db, 'business_welcome_texts', businessSlug);
      const docSnap = await getDoc(docRef);

      if (docSnap.exists()) {
        const data = docSnap.data();
        const text = data.text || data.welcomeText || '';
        setWelcomeText(text);
        setOriginalText(text);
      } else {
        // No existing text, use default
        const defaultText = 'ברוכים הבאים! אנו שמחים לארח אתכם ולספק לכם את השירות הטוב ביותר.';
        setWelcomeText(defaultText);
        setOriginalText(defaultText);
      }
    } catch (err) {
      console.error('Error loading welcome text:', err);
      setError('שגיאה בטעינת הטקסט. אנא נסה שוב.');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    if (!businessSlug) {
      setError('חסר מזהה עסק');
      return;
    }

    try {
      setSaving(true);
      setError(null);
      setSuccessMessage('');

      const db = getFirestore();
      const docRef = doc(db, 'business_welcome_texts', businessSlug);

      await setDoc(docRef, {
        text: welcomeText,
        updatedAt: new Date().toISOString(),
        updatedBy: user.email || user.uid
      });

      setOriginalText(welcomeText);
      setIsEditing(false);
      setSuccessMessage('הטקסט נשמר בהצלחה!');

      // Clear success message after 3 seconds
      setTimeout(() => {
        setSuccessMessage('');
      }, 3000);
    } catch (err) {
      console.error('Error saving welcome text:', err);
      setError('שגיאה בשמירת הטקסט. אנא נסה שוב.');
    } finally {
      setSaving(false);
    }
  };

  const handleCancel = () => {
    setWelcomeText(originalText);
    setIsEditing(false);
    setError(null);
  };

  const handleEdit = () => {
    setIsEditing(true);
    setError(null);
    setSuccessMessage('');
  };

  if (loading) {
    return (
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex items-center gap-2 mb-4">
          <MessageSquare className="w-5 h-5 text-indigo-600" />
          <h2 className="text-lg font-semibold text-gray-900">טקסט פתיחה</h2>
        </div>
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-500"></div>
          <span className="mr-3 text-gray-600">טוען...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-lg shadow p-6">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <MessageSquare className="w-5 h-5 text-indigo-600" />
          <h2 className="text-lg font-semibold text-gray-900">טקסט פתיחה</h2>
        </div>

        {!isEditing && (
          <button
            onClick={handleEdit}
            className="flex items-center gap-2 px-3 py-1.5 text-sm font-medium text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50 rounded-lg transition-colors"
          >
            <Edit2 className="w-4 h-4" />
            ערוך
          </button>
        )}
      </div>

      {/* Description */}
      <p className="text-sm text-gray-600 mb-4">
        הטקסט המוצג ללקוחות במסך הפתיחה של מערכת קביעת התורים
      </p>

      {/* Error Message */}
      {error && (
        <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <span className="text-sm text-red-800">{error}</span>
        </div>
      )}

      {/* Success Message */}
      {successMessage && (
        <div className="mb-4 p-3 bg-green-50 border border-green-200 rounded-lg flex items-start gap-2">
          <AlertCircle className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <span className="text-sm text-green-800">{successMessage}</span>
        </div>
      )}

      {/* Text Display/Edit */}
      {isEditing ? (
        <>
          <textarea
            value={welcomeText}
            onChange={(e) => setWelcomeText(e.target.value)}
            className="w-full min-h-[200px] p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent resize-y text-right"
            dir="rtl"
            placeholder="הזן את טקסט הפתיחה..."
          />

          {/* Character Count */}
          <div className="mt-2 text-sm text-gray-500 text-left">
            {welcomeText.length} תווים
          </div>

          {/* Action Buttons */}
          <div className="mt-4 flex gap-3 justify-end">
            <button
              onClick={handleCancel}
              disabled={saving}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              <X className="w-4 h-4" />
              ביטול
            </button>

            <button
              onClick={handleSave}
              disabled={saving || welcomeText === originalText}
              className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {saving ? (
                <>
                  <div className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin"></div>
                  שומר...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  שמור
                </>
              )}
            </button>
          </div>
        </>
      ) : (
        <div className="p-4 bg-gray-50 rounded-lg border border-gray-200">
          <p className="text-gray-800 whitespace-pre-line text-right" dir="rtl">
            {welcomeText || 'אין טקסט פתיחה'}
          </p>
        </div>
      )}
    </div>
  );
}
